import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { DateService } from '../shared/services/date.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';

/**
 * Service that provides welcome content for hero module.
 */
@Injectable({
    providedIn: 'root'
})
export class WelcomeContentService {

    private static readonly MorningStartHour = 4;
    private static readonly AfternoonStartHour = 12;
    private static readonly EveningStartHour = 18;

    private readonly greetingSubject: BehaviorSubject<string>;
    private currentGreetingText: string;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly dateService: DateService,
        private readonly locService: LocService,
        private readonly userContextService: UserContextService
    ) {
        this.currentGreetingText = this.generateGreeting();
        this.greetingSubject = new BehaviorSubject(this.currentGreeting);

        // Setup timers to update greeting based on time of the day
        this.setupTimer(WelcomeContentService.MorningStartHour);
        this.setupTimer(WelcomeContentService.AfternoonStartHour);
        this.setupTimer(WelcomeContentService.EveningStartHour);
    }

    public get greetingObservable(): Observable<string> {
        return this.greetingSubject.asObservable();
    }

    public get currentGreeting(): string {
        return this.currentGreetingText;
    }

    private setupTimer(hourOfDay: number) {
        const now = new Date();
        const hourOfDayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hourOfDay, 0, 0, 0);
        if (hourOfDayDate < now) {
            hourOfDayDate.setUTCDate(hourOfDayDate.getUTCDate() + 1);
        }

        const timeout = hourOfDayDate.getTime() - now.getTime();
        setTimeout(() => {
            this.updateGreeting();
            this.setupTimer(hourOfDay);
        }, timeout);
    }

    private updateGreeting() {
        const greeting = this.generateGreeting();
        if (greeting !== this.currentGreetingText) {
            this.currentGreetingText = greeting;
            this.greetingSubject.next(this.currentGreetingText);
        }
    }

    private generateGreeting(): string {
        const displayName = this.userContextService.user.displayName;
        const current = this.dateService.getCurrentDate();
        const hours = current.getHours();
        let greeting: string;

        if (this.capabilitiesService.capabilities.greetingRefreshEnabled) {
            if (displayName) {
                greeting = this.locService.format(this.locService.WelcomeGreetingWithName, displayName);
            } else {
                greeting = this.locService.WelcomeGreetingNoName;
            }
        } else {
            if (hours >= WelcomeContentService.MorningStartHour && hours < WelcomeContentService.AfternoonStartHour) {
                greeting = displayName ? this.locService.WelcomeMorningWithName : this.locService.WelcomeMorningNoName;
            } else if (hours >= WelcomeContentService.AfternoonStartHour && hours < WelcomeContentService.EveningStartHour) {
                greeting = displayName ? this.locService.WelcomeAfternoonWithName : this.locService.WelcomeAfternoonNoName;
            } else {
                greeting = displayName ? this.locService.WelcomeEveningWithName : this.locService.WelcomeEveningNoName;
            }

            if (displayName) {
                greeting = this.locService.format(greeting, displayName);
            }
        }

        return greeting;
    }
}
